// ============================================================================
// RÉGLAGES → OPÉRATEURS & DROITS — qui ouvre la caisse, et ce qu'il a le droit d'y faire.
//
// UTILITÉ : ces comptes ne se réglaient que sur la tablette, devant le comptoir. Une enseigne ne
// pouvait ni voir ses habilitations d'un coup d'œil, ni retirer un droit à distance.
//
// ⚠️ TROIS ÉTATS, PAS DEUX (§26.5). « Code responsable » est celui qui fait vivre un magasin : la
// caisse accepte, mais après le PIN d'un responsable, NOMINATIF, et le journal retient qui a
// autorisé quoi. ⚠️ Il s'appelait « À valider » — juste, mais muet sur QUI valide et COMMENT ;
// seul le mot d'écran a changé, la valeur `ask` reste celle qui est persistée et qui descend.
// Le catalogue des droits vit dans le contrat (`@orgazone/sync-contract`), parce que le back-office
// qui les propose et la caisse qui les applique doivent lire la MÊME règle.
//
// ⚠️ CE QUE LA CAISSE N'APPLIQUE PAS ENCORE EST DIT, droit par droit. Trente-quatre interrupteurs
// dont deux agissent laisseraient croire à trente-quatre : c'est l'interdit n° 6 du §23.0. Le réglage
// n'est pas perdu — il est écrit et il DESCEND par le canal `operators` — mais il attend la caisse,
// et l'écran doit le dire plutôt que de le laisser croire ou de retirer le réglage.
//
// ⚠️ AUCUN PIN NE REDESCEND. La liste montre « PIN •••• » parce que le compte EN A un, jamais lequel.
// Un code à quatre chiffres identifie à lui seul en caisse : l'afficher reviendrait à distribuer les
// identités du magasin à quiconque ouvre cet écran.
// ============================================================================
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  RIGHT_DOMAINS, RIGHT_TEMPLATES, TEMPLATE_SUMMARY, REGISTER_ENFORCED,
  isEnforcedByRegister, isRefusedByRegister, rightsTally, templateValue,
  type RightDef, type RightDomain, type RightValue, type TemplateKey,
} from '@orgazone/sync-contract';
import { alert, confirm, EmptyState, showToast, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { api, type RightsTemplateView, type RosterOperator, type RosterView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { templateName } from '../rights-templates';

// ── LA CHARTE DE CET ÉCRAN, portée du dessin ────────────────────────────────────────────────────
const CARD: CSSProperties = {
  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden',
};
const LABEL: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.09em',
  textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600, marginBottom: 7,
};

/** Une pastille — le seul dessin de badge de l'écran, décliné par teinte. */
function Pill({ bg, fg, children, height = 22 }: { bg: string; fg: string; children: ReactNode; height?: number }) {
  return (
    <span style={{
      height, padding: '0 10px', borderRadius: 'var(--r-pill)', background: bg, color: fg,
      fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>{children}</span>
  );
}

/** Une icône du catalogue — tracé SVG porté par la donnée, pas un jeton de charte. */
function Trace({ d, size = 15, width = 1.9 }: { d: string; size?: number; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
  );
}

const TEMPLATE_TINT: Record<TemplateKey, { bg: string; fg: string }> = {
  vend: { bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  resp: { bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  ger: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
};
/** ⚠️ Un compte sans modèle n'est pas un vendeur : il est ANTÉRIEUR au catalogue, et ça se dit. */
/** ⚠️ Sans rôle, aucun droit ne se résout : le compte ouvre une caisse où rien ne lui est permis. */
const NO_TEMPLATE = { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', label: key('Aucun rôle') };

/** L'icône d'un modèle — celle du modèle de l'éditeur dont il part. */
const templateIcon = (base: TemplateKey): string => RIGHT_TEMPLATES.find((x) => x.key === base)!.icon;

/** Le nom du modèle d'un compte, ou « Aucun rôle ». */
const roleName = (templates: readonly RightsTemplateView[], id: string | null): string => {
  const tpl = id ? templates.find((x) => x.id === id) : undefined;
  return tpl ? templateName(tpl) : t(NO_TEMPLATE.label);
};



/**
 * UN COMPTE EST-IL UTILISABLE EN CAISSE ? — il lui faut un CODE **et** un RÔLE.
 *
 * ⚠️ LES DEUX, ET PAS L'UN OU L'AUTRE. Sans code, personne ne peut se connecter ; sans rôle, aucun
 * droit n'est résolu et le compte ouvre une caisse où rien ne lui est permis. Les deux mènent au même
 * endroit — un compte qui ne sert à rien — donc l'écran les dit de la même façon.
 * ⚠️ ÉCRITE UNE FOIS, LUE À DEUX ENDROITS (la liste et la fiche) : deux formulations divergeraient, et
 * la liste finirait par annoncer « valide » sur une fiche qui dit le contraire.
 */
function accountValid(o: RosterOperator): boolean {
  return o.hasPin && o.template !== null;
}

export function Operateurs() {
  const [view, setView] = useState<RosterView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState<'ops' | 'tpl'>('ops');
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<'all' | 'scan' | 'over' | 'off'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [openTemplate, setOpenTemplate] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const load = useCallback(async () => {
    try { setFailure(null); setView(await api.operators()); }
    catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Memoises pour que les listes derivees ne se recalculent pas a chaque frappe : sans cela, un
  // tableau neuf a chaque rendu invalide tous les memos qui en dependent.
  const all = useMemo(() => view?.operators ?? [], [view]);
  const templates = useMemo(() => view?.templates ?? [], [view]);
  const active = useMemo(() => all.filter((o) => o.active), [all]);
  const overCount = (o: RosterOperator) => Object.keys(o.overrides).length;

  const kpis = useMemo(() => [
    {
      label: t('Opérateurs actifs'), value: String(active.length), note: t('comptes de caisse de ce magasin'),
      icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0', bg: 'var(--teal-100)', fg: 'var(--teal-800)',
    },
    {
      label: t('Habilités Mobile Partner'), value: String(active.filter((o) => o.scan).length),
      note: t('les droits suivent l’opérateur, pas l’appareil'),
      icon: 'M4 7V5a1 1 0 0 1 1-1h2M17 4h2a1 1 0 0 1 1 1v2M20 17v2a1 1 0 0 1-1 1h-2M7 20H5a1 1 0 0 1-1-1v-2M7 12h10',
      bg: 'var(--violet-bg)', fg: 'var(--violet-fg)',
    },
    {
      label: t('Droits surchargés'), value: String(active.filter((o) => overCount(o) > 0).length),
      note: t('comptes qui s’écartent de leur modèle'),
      icon: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z',
      bg: 'var(--amber-bg)', fg: 'var(--amber-fg)',
    },
    {
      label: t('Comptes désactivés'), value: String(all.length - active.length), note: t('historique de ventes conservé'),
      icon: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9', bg: 'var(--ink-100)', fg: 'var(--ink-500)',
    },
  ], [all, active]);

  const chips = [
    { v: 'all' as const, label: t('Actifs'), n: active.length },
    { v: 'scan' as const, label: t('Mobile Partner'), n: all.filter((o) => o.scan).length },
    { v: 'over' as const, label: t('Surchargés'), n: all.filter((o) => overCount(o) > 0).length },
    { v: 'off' as const, label: t('Désactivés'), n: all.length - active.length },
  ];

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((o) => {
      if (chip === 'all' && !o.active) return false;
      if (chip === 'scan' && !o.scan) return false;
      if (chip === 'over' && overCount(o) === 0) return false;
      if (chip === 'off' && o.active) return false;
      if (!q) return true;
      return o.name.toLowerCase().includes(q) || roleName(templates, o.template).toLowerCase().includes(q);
    });
  }, [all, chip, query, templates]);

  const opened = openId ? all.find((o) => o.id === openId) ?? null : null;

  /** Enregistre les droits d'un compte, puis relit — l'écran montre la base, pas ce qu'il croit. */
  const saveRights = useCallback(async (id: string, template: string, overrides: Record<string, RightValue>) => {
    setBusy(true);
    try {
      setView(await api.setOperatorRights(id, template, overrides));
      showToast(t('Droits enregistrés — ils descendent aux caisses.'));
    } catch (e) {
      await alert({ title: t('Droits refusés'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }, []);

  /**
   * Réapplique les modèles à tous les comptes — le geste qui fait descendre un changement du catalogue des droits sur
   * les comptes déjà réglés. Le serveur dit combien de comptes il a réécrits ; l'écran relit le roster.
   */
  const reapply = useCallback(async () => {
    setBusy(true);
    try {
      const done = await api.reapplyRightsTemplates();
      setView(done.roster);
      showToast(done.updated > 0
        ? t('{n} compte remis à son modèle — les droits descendent aux caisses.', { n: done.updated })
        : t('Tous les comptes suivent déjà leur modèle.'));
    } catch (e) {
      await alert({ title: t('Réapplication refusée'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }, []);

  /**
   * Supprime un compte que rien ne désigne.
   * ⚠️ LA CONFIRMATION DIT CE QUI DISPARAÎT, pas « êtes-vous sûr ? » : un tombstone descend à toutes
   *    les caisses du magasin et ne se retire pas.
   * ⚠️ LE VERDICT APPARTIENT AU SERVEUR : il mesure les douze tables qui portent un opérateur, dans
   *    la même transaction. Le bouton n'est qu'un filtre de bon sens ; le refus, lui, nomme ce qui bloque.
   */
  const supprimer = useCallback(async (o: RosterOperator) => {
    const ok = await confirm({
      title: t('Supprimer « {name} » ?', { name: o.name }),
      message: t('Ce compte n’a aucune vente à son nom. La suppression descend à toutes les caisses du magasin et ne s’annule pas. Si le compte a servi à autre chose (ouverture de caisse, comptage, journal fiscal), le serveur refusera et vous dira quoi.'),
      confirmLabel: t('Supprimer'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      setView(await api.deleteOperator(o.id));
      setOpenId(null);
      showToast(t('Compte supprimé — la suppression descend aux caisses.'));
    } catch (e) {
      await alert({ title: t('Suppression refusée'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }, []);

  /** Remplace le code d'un compte ; rend `true` seulement si le serveur l'a ACCEPTÉ — la boîte du code s'y règle. */
  const setPin = useCallback(async (id: string, pin: string): Promise<boolean> => {
    setBusy(true);
    try {
      setView(await api.setOperatorPin(id, pin));
      // ⚠️ On ne répète PAS le code dans le message : un toast se lit par-dessus l'épaule.
      showToast(t('Code remplacé — communiquez-le à l’opérateur, il n’est plus lisible ensuite.'), 'ok', 7000);
      return true;
    } catch (e) {
      await alert({ title: t('Code refusé'), message: e instanceof Error ? e.message : String(e) });
      return false;
    } finally { setBusy(false); }
  }, []);

  const setActive = useCallback(async (id: string, next: boolean) => {
    setBusy(true);
    try {
      setView(await api.setOperatorActive(id, next));
      showToast(next ? t('Compte réactivé.') : t('Compte désactivé — son historique de ventes reste lisible.'));
    } catch (e) {
      await alert({ title: t('Refusé'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }, []);

  if (failure !== null) {
    return (
      <div className="dcard" style={{ padding: 18 }}>
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
          {t('Le roster de ce magasin n’a pas pu être lu : {failure}', { failure })}
        </p>
      </div>
    );
  }
  if (!view) return <div className="dcard" style={{ padding: 18, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</div>;

  return (
    <div>
      {/* ── EN-TÊTE ────────────────────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
              
              {t('Opérateurs & droits')}
            </h1>
          </div>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Les comptes de <1>caisse et de Mobile Partner</1> — un compte par magasin. Les comptes du back-office sont multi-magasins et se gèrent à part.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button type="button" className="btn-ghost" disabled={busy} onClick={() => { void reapply(); }} style={{ height: 40 }}
          title={t('Recalcule les droits de chaque compte depuis son modèle — après un changement du catalogue des droits.')}>
          {t('Réappliquer les modèles')}
        </button>
        <button type="button" className="btn-cta" onClick={() => setCreating(true)} style={{ height: 40 }}>
          
          {t('Nouvel opérateur')}
        </button>
      </div>

      {/* ── LES QUATRE REPÈRES ─────────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(185px,1fr))', gap: 8, marginBottom: 10 }}>
        {kpis.map((k) => (
          <div key={k.label} title={k.note} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', minWidth: 0,
          }}>
            <span style={{
              width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: k.bg, color: k.fg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><Trace d={k.icon} /></span>
            <span style={{ minWidth: 0 }}>
              <span style={{
                display: 'block', fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em',
                textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{k.label}</span>
              <span style={{
                display: 'block', fontSize: 'var(--t-strong)', fontWeight: 800, color: 'var(--ink-900)',
                fontVariantNumeric: 'tabular-nums', marginTop: 1, whiteSpace: 'nowrap',
              }}>{k.value}</span>
            </span>
          </div>
        ))}
      </div>

      {/* ── LES DEUX ONGLETS ───────────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {([['ops', t('Opérateurs'), String(active.length)], ['tpl', t('Modèles de droits'), String(templates.length)]] as const).map(([v, l, n]) => (
          <button aria-pressed={tab === v} key={v} type="button" onClick={() => setTab(v)} style={{
            height: 36, padding: '0 16px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
            border: `1px solid ${tab === v ? 'var(--teal-400)' : 'var(--ink-200)'}`,
            background: tab === v ? 'var(--teal-100)' : 'var(--paper)',
            color: tab === v ? 'var(--teal-800)' : 'var(--ink-600)',
            fontSize: 'var(--t-lead)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>{l}<span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{n}</span></button>
        ))}
      </div>

      {tab === 'ops' ? (
        <div style={CARD}>
          {/* Barre d'outils : recherche, puces de filtre, compte. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px',
            borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap',
          }}>
            <input className="field-input" style={{ height: 36, flex: '0 1 260px', minWidth: 190 }}
              value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Nom, rôle…')} />
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {chips.map((c) => (
                <button aria-pressed={chip === c.v} key={c.v} type="button" onClick={() => setChip(c.v)} style={{
                  height: 30, padding: '0 11px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                  border: `1px solid ${chip === c.v ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                  background: chip === c.v ? 'var(--teal-100)' : 'var(--paper)',
                  color: chip === c.v ? 'var(--teal-800)' : 'var(--ink-600)',
                  fontSize: 'var(--t-dense)', fontWeight: 600, display: 'inline-flex', alignItems: 'center',
                  gap: 6, whiteSpace: 'nowrap',
                }}>{c.label}<span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>{c.n}</span></button>
              ))}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
              {rows.length === all.length ? t('{n} compte', { n: all.length }) : t('{n} sur {n2}', { n: rows.length, n2: all.length })}
            </span>
          </div>

          <RosterTable
            rows={rows}
            templates={templates}
            total={all.length}
            onOpen={setOpenId}
            onReset={() => { setQuery(''); setChip('all'); }}
            onCreate={() => setCreating(true)}
          />
        </div>
      ) : (
        <TemplatesTab templates={templates} onOpen={setOpenTemplate} onCreate={() => setCreatingTemplate(true)} />
      )}

      {opened && (
        <OperatorSheet
          operator={opened}
          templates={templates}
          busy={busy}
          onClose={() => setOpenId(null)}
          onSave={saveRights}
          onToggleActive={setActive}
          onSetPin={setPin}
          onDelete={supprimer}
        />
      )}
      {openTemplate && templates.some((x) => x.id === openTemplate) && (
        <TemplateSheet template={templates.find((x) => x.id === openTemplate)!}
          onChanged={setView} onClose={() => setOpenTemplate(null)} />
      )}
      {creating && (
        <NewOperator
          templates={templates}
          onClose={() => setCreating(false)}
          onCreated={(v) => { setView(v); setCreating(false); }}
        />
      )}
      {creatingTemplate && (
        <NewTemplate
          templates={templates}
          onClose={() => setCreatingTemplate(false)}
          onCreated={(v, id) => { setView(v); setCreatingTemplate(false); setOpenTemplate(id); }}
        />
      )}
    </div>
  );
}

// ── LA LISTE ────────────────────────────────────────────────────────────────────────────────────

const GRID = 'minmax(190px,1fr) 134px 92px 100px 112px 96px';

/**
 * L'ÉTAT VIDE — celui de la CHARTE (`EmptyState`), pas un texte centré à la main.
 *
 * ⚠️ DEUX VIDES QUI N'ONT RIEN À VOIR, ET C'EST TOUT L'INTÉRÊT DE LES SÉPARER. Un magasin SANS AUCUN
 * compte est une installation neuve : la phrase utile est « créez le premier », et le geste est là,
 * sous la note. Un filtre qui ne rend RIEN est un magasin plein où l'on cherche mal : la phrase utile
 * est « retirez le filtre », et le geste le retire vraiment. Les confondre — c'était le cas — envoie
 * un exploitant chercher un filtre qu'il n'a pas posé, ou lui propose de créer un compte alors qu'il
 * en a huit sous les yeux (§23.0 : l'écran dit ce qui est, et nomme le geste suivant).
 */
function RosterEmpty({ filtered, onReset, onCreate }: {
  filtered: boolean; onReset: () => void; onCreate: () => void;
}) {
  if (filtered) {
    return (
      <EmptyState
        icon="search"
        title={t('Aucun opérateur ne correspond')}
        note={t('Ce magasin a des comptes, mais aucun ne passe le filtre en cours. Essayez un autre nom, ou revenez à la liste complète.')}
        action={<button type="button" className="btn-ghost" onClick={onReset}>{t('Retirer les filtres')}</button>}
      />
    );
  }
  return (
    <EmptyState
      icon="users"
      title={t('Aucun compte de caisse dans ce magasin')}
      note={<><Trans k='Personne ne peut encore ouvrir la caisse ici. Un compte se crée avec un <1>nom</1>, un <2>modèle de droits</2> et un <3>code PIN</3> — il descend ensuite aux tablettes du magasin.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></>}
      action={<button type="button" className="btn-cta" onClick={onCreate}>{t('Créer le premier opérateur')}</button>}
    />
  );
}

function RosterTable({ rows, templates, total, onOpen, onReset, onCreate }: {
  rows: RosterOperator[];
  templates: readonly RightsTemplateView[];
  /** Le roster ENTIER — c'est lui qui distingue « rien à montrer » de « rien qui corresponde ». */
  total: number;
  onOpen: (id: string) => void;
  onReset: () => void;
  onCreate: () => void;
}) {
  const head: CSSProperties = {
    display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '9px 14px',
    background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', minWidth: 820,
    fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
    color: 'var(--ink-500)',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      {/* ⚠️ PAS DE BANDEAU DE COLONNES SUR UN MAGASIN SANS AUCUN COMPTE : six en-têtes au-dessus du
          vide donnent un tableau cassé, et le magasin n'a rien à ranger en colonnes. Il RESTE quand
          c'est un FILTRE qui vide la liste — là les colonnes ont un sens, on vient d'y trier. */}
      {(rows.length > 0 || total > 0) && (
        <div style={head}>
          {[t('Opérateur'), t('Rôle'), t('Ventes'), t('Mobile Partner'), t('Droits'), t('État')].map((h) => <span key={h}>{h}</span>)}
        </div>
      )}
      <div style={{
        maxHeight: 'calc(100vh - 336px)', minHeight: 280, overflowY: 'auto',
        // ⚠️ La largeur plancher ne vaut que pour des LIGNES : sur un état vide, elle imposerait un
        //    défilement horizontal à un contenu centré qui tient dans n'importe quelle fenêtre.
        minWidth: rows.length ? 820 : undefined,
        display: rows.length ? undefined : 'grid', placeItems: rows.length ? undefined : 'center',
      }}>
        {rows.map((o) => {
          const over = Object.keys(o.overrides).length;
          const followed = o.template ? templates.find((x) => x.id === o.template) : undefined;
          const tint = followed ? TEMPLATE_TINT[followed.base] : NO_TEMPLATE;
          return (
            <div key={o.id} onClick={() => onOpen(o.id)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(o.id); } }}
              style={{
                display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '8px 14px',
                borderBottom: '1px solid var(--ink-100)', cursor: 'pointer', opacity: o.active ? 1 : 0.55,
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span style={{
                  width: 32, height: 32, borderRadius: 'var(--r-pill)', flexShrink: 0,
                  background: o.active ? 'var(--teal-700)' : 'var(--ink-200)',
                  color: o.active ? '#ffffff' : 'var(--ink-500)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'var(--t-meta)', fontWeight: 700,
                }}>{o.initials}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{
                    display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{o.name}</span>
                  {/* ⚠️ « NON VALIDE » DIT L'ÉTAT, PAS LA MÉCANIQUE. La ligne annonçait « la caisse le
                      refuse » : c'est vrai, mais c'est la conséquence, et elle se lit mal. Ce qu'un
                      gérant cherche du regard, c'est quels comptes sont utilisables. */}
                  <span style={{
                    display: 'block', fontSize: 'var(--t-meta)',
                    color: accountValid(o) ? 'var(--ink-500)' : 'var(--amber-fg)',
                  }}>
                    {accountValid(o) ? t('Compte valide') : t('Compte non valide')}
                    {'  ·  '}{o.scan ? t('caisse + Mobile Partner') : t('caisse')}
                  </span>
                </span>
              </span>
              <span><Pill bg={tint.bg} fg={tint.fg}>{roleName(templates, o.template)}</Pill></span>
              <span style={{
                fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums',
              }}>{o.sales.toLocaleString('fr-FR')}</span>
              <span>
                {o.scan
                  ? <Pill bg="var(--violet-bg)" fg="var(--violet-fg)" height={20}>{t('Mobile Partner')}</Pill>
                  : <span style={{ color: 'var(--ink-400)' }}>—</span>}
              </span>
              <span style={{ fontSize: 'var(--t-meta)', color: over ? 'var(--amber-fg)' : 'var(--ink-500)' }}>
                {over ? t('{n} exception', { n: over }) : t('modèle')}
              </span>
              <span>
                <Pill bg={o.active ? 'var(--teal-100)' : 'var(--ink-100)'} fg={o.active ? 'var(--teal-800)' : 'var(--ink-500)'}>
                  {o.active ? t('Actif') : t('Désactivé')}
                </Pill>
              </span>
            </div>
          );
        })}
        {rows.length === 0 && (
          <RosterEmpty filtered={total > 0} onReset={onReset} onCreate={onCreate} />
        )}
      </div>
    </div>
  );
}

// ── LES LIGNES DE DROIT, partagées par la fiche et le modèle ────────────────────────────────────

const SEGMENTS: { v: RightValue; label: Translatable; note: Translatable }[] = [
  { v: 'no', label: key('Interdit'), note: key('La caisse refuse, même avec un responsable') },
  { v: 'ask', label: key('Code responsable'), note: key('Autorisé après le PIN d’un responsable — nominatif, tracé') },
  { v: 'yes', label: key('Libre'), note: key('Autorisé sans rien demander') },
];
const SEG_TINT: Record<RightValue, { bg: string; fg: string }> = {
  no: { bg: 'var(--ink-100)', fg: 'var(--ink-700)' },
  ask: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  yes: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
};

/**
 * UNE LIGNE DE DROIT — relevée du dessin, mode par mode.
 *
 * ⚠️ UN DROIT BOOLÉEN EST UN INTERRUPTEUR, PAS DEUX SEGMENTS. Le dessin distingue les deux formes, et
 * la distinction porte du sens : deux segments « Interdit / Libre » se lisent comme un choix entre
 * deux options de même rang, là où un interrupteur dit « c'est ouvert, ou ça ne l'est pas ». La
 * confusion faisait aussi ressembler un droit à deux états à un droit à trois états amputé.
 */
function RightRow({ def, value, overridden, readOnly, revertTo = 'template', onSet, onRevert }: {
  def: RightDef;
  value: RightValue | null;
  overridden: boolean;
  readOnly: boolean;
  /** Où ramène le bouton de retour : au modèle (la fiche d'un compte), à la valeur de l'éditeur (la fenêtre d'un modèle). */
  revertTo?: 'template' | 'editor';
  onSet: (v: RightValue) => void;
  onRevert: () => void;
}) {
  const applied = isEnforcedByRegister(def.key);
  // ⚠️ LE SEUL CAS OÙ LES DEUX PASTILLES SE CONTREDIRAIENT : sur un droit que la caisse applique
  //    déjà SANS savoir en demander le code (hors `REGISTER_ASKS`), « Code responsable » REFUSE.
  //    Afficher « appliqué en caisse » à côté ferait croire au geste promis par le mot (§23.0). Un droit
  //    que la caisse sait demander ne déclenche rien : le code d'un responsable y ouvre le geste.
  const refused = value !== null && isRefusedByRegister(def.key, value);
  const on = value === 'yes';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
      borderBottom: '1px solid var(--ink-100)',
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{t(def.label)}</span>
          {overridden && (
            <span style={{
              height: 17, padding: '0 7px', borderRadius: 'var(--r-pill)',
              background: 'var(--amber-bg)', color: 'var(--amber-fg)',
              fontSize: 'var(--t-eyebrow)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
            }}>{t('surchargé')}</span>
          )}
          {/* ⚠️ ON MARQUE LES DEUX QUI AGISSENT, PAS LES VINGT-HUIT QUI ATTENDENT : un avertissement
              répété trente fois ne se lit plus. L'écart est dit UNE fois, en tête de la colonne. */}
          {applied && (
            <span style={{
              height: 17, padding: '0 7px', borderRadius: 'var(--r-pill)',
              background: refused ? 'var(--amber-bg)' : 'var(--teal-100)',
              color: refused ? 'var(--amber-fg)' : 'var(--teal-800)',
              fontSize: 'var(--t-eyebrow)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
            }}>{refused ? t('la caisse refuse — elle ne sait pas encore demander le code') : t('appliqué en caisse')}</span>
          )}
        </span>
        <span style={{
          display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)',
          marginTop: 2, lineHeight: 1.45,
        }}>{t(def.note)}</span>
      </span>

      {def.mode === 'backoffice' && (
        <span style={{
          height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)',
          background: 'var(--ink-100)', color: 'var(--ink-500)',
          fontSize: 'var(--t-meta)', fontWeight: 600, display: 'inline-flex', alignItems: 'center',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>{t('Back-office')}</span>
      )}

      {def.mode === 'boolean' && (
        <button type="button" role="switch" aria-checked={on} aria-label={t(def.label)} disabled={readOnly}
          onClick={() => onSet(on ? 'no' : 'yes')}
          style={{
            position: 'relative', width: 38, height: 22, border: 'none', padding: 0,
            borderRadius: 'var(--r-pill)', flexShrink: 0,
            background: on ? 'var(--teal-500)' : 'var(--ink-300)',
            transition: 'background .15s', cursor: readOnly ? 'not-allowed' : 'pointer',
          }}>
          <span style={{
            position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16,
            borderRadius: 'var(--r-pill)', background: 'var(--paper)',
            boxShadow: '0 1px 3px rgba(12,14,16,.3)', transition: 'left .15s',
          }} />
        </button>
      )}

      {def.mode === 'tri' && (
        // ⚠️ UN GROUPE NOMMÉ PAR SON DROIT, des boutons qui disent lequel est choisi : sans eux, une aide technique
        //    lisait trois boutons « Interdit · Code responsable · Libre » sans dire de quel droit ni lequel s'applique.
        <span role="group" aria-label={t(def.label)} style={{
          display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
          overflow: 'hidden', flexShrink: 0,
        }}>
          {SEGMENTS.map((s) => {
            const actif = value === s.v;
            return (
              <button key={s.v} type="button" title={t(s.note)} aria-pressed={actif} disabled={readOnly}
                onClick={() => onSet(s.v)} style={{
                  height: 30, padding: '0 10px', border: 'none',
                  background: actif ? SEG_TINT[s.v].bg : 'transparent',
                  color: actif ? SEG_TINT[s.v].fg : 'var(--ink-500)',
                  fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap',
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                }}>{t(s.label)}</button>
            );
          })}
        </span>
      )}

      {overridden && !readOnly && (
        <button type="button" title={revertTo === 'editor' ? t('Revenir à la valeur de l’éditeur') : t('Revenir au modèle')}
          aria-label={revertTo === 'editor'
            ? t('Revenir à la valeur de l’éditeur — {droit}', { droit: t(def.label) })
            : t('Revenir au modèle — {droit}', { droit: t(def.label) })} onClick={onRevert}
          style={{
            width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)',
            background: 'var(--ink-100)', color: 'var(--ink-600)', flexShrink: 0, padding: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}><Trace d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" size={13} width={2} /></button>
      )}
    </div>
  );
}

/**
 * UN DOMAINE = UN TABLEAU À PART, avec son en-tête — c'est le dessin, et c'est ce qui rend la liste
 * lisible : trente droits d'affilée ne se parcourent pas, huit blocs de trois à neuf lignes, oui.
 * ⚠️ La DERNIÈRE ligne perd son filet : sinon il doublerait la bordure du bloc.
 */
function RightDomainCard({ domain, tally, children }: {
  domain: RightDomain;
  /** Le bilan du domaine (libres, sous code responsable, interdits) — la fenêtre d'un modèle le montre en tête. */
  tally?: { yes: number; ask: number; no: number };
  children: ReactNode;
}) {
  return (
    <div data-domaine={domain.key} style={{
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden',
      // ⚠️ JAMAIS COMPRIMÉE : la fiche d'un modèle range ces cartes dans une colonne de hauteur fixe qui défile. Avec
      //    `overflow: hidden`, une carte n'a plus de hauteur minimale, et le navigateur les écrasait pour tout faire
      //    tenir — des domaines entiers sans aucune ligne visible, au lieu d'une zone qui défile.
      flexShrink: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
        background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{ display: 'flex', color: 'var(--ink-500)', flexShrink: 0 }}>
          <Trace d={domain.icon} size={14} />
        </span>
        <span style={{
          flex: 1, minWidth: 160, fontSize: 'var(--t-meta)', fontWeight: 700, letterSpacing: '.05em',
          textTransform: 'uppercase', color: 'var(--ink-600)',
        }}>{t(domain.label)}</span>
        {/* Un compte à zéro ne se montre pas : trois pastilles dont une vide ne disent rien de plus que deux. */}
        {tally && (
          <span data-testid="bilan-domaine" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {tally.yes > 0 && <Pill bg="var(--teal-100)" fg="var(--teal-800)" height={20}>{t('{n} libre', { n: tally.yes })}</Pill>}
            {tally.ask > 0 && <Pill bg="var(--amber-bg)" fg="var(--amber-fg)" height={20}>{t('{n} sous code responsable', { n: tally.ask })}</Pill>}
            {tally.no > 0 && <Pill bg="var(--ink-100)" fg="var(--ink-600)" height={20}>{t('{n} interdit', { n: tally.no })}</Pill>}
          </span>
        )}
      </div>
      <div style={{ /* ⚠️ le filet de la dernière ligne est retiré ici, pas ligne par ligne */ }}>
        {children}
      </div>
    </div>
  );
}

const RIGHTS_TOTAL = RIGHT_DOMAINS.flatMap((d) => d.rights).filter((r) => r.mode !== 'backoffice').length;

/**
 * CE QUE LA VERSION DE CAISSE DÉPLOYÉE APPLIQUE VRAIMENT — dit UNE fois, en tête, avant les réglages.
 * ⚠️ C'est le seul endroit où l'écart se dit en entier. Sans lui, trente interrupteurs dont deux
 * agissent laisseraient croire à trente (§23.0, interdit n° 6) ; répété sur chaque ligne, il
 * deviendrait un bruit qu'on ne lit plus.
 */
function RightsNotice() {
  return (
    <div style={{
      display: 'flex', gap: 9, padding: '10px 13px',
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
      background: 'var(--amber-bg)', color: 'var(--amber-fg)',
      fontSize: 'var(--t-meta)', lineHeight: 1.55,
    }}>
      <Trace d="M12 9v4M12 17v.01M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" size={14} width={2} />
      <span>
        <Trans k='<1>{n} de ces {droits}</1> sont appliqués par la version de caisse déployée — ils portent la pastille « appliqué en caisse ». Les autres s’enregistrent et <2>descendent aux tablettes</2>, mais n’y changent rien pour l’instant.' vars={{ n: REGISTER_ENFORCED.length, droits: t('{n} droit', { n: RIGHTS_TOTAL }) }} parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
    </div>
  );
}

/** Le pied qui dit, une fois pour toutes, ce que « code responsable » veut dire — et où il en est. */
function RightsFooter() {
  return (
    <div style={{
      display: 'flex', gap: 9, padding: '11px 13px', marginTop: 4,
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
      fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55,
    }}>
      <Trace d="M8 11V7a4 4 0 0 1 8 0v4" size={13} width={2} />
      <span>
        <Trans k='<1>Code responsable</1> = la caisse demande le PIN d’un responsable ou plus, <2>nominatif</2> : le journal retient qui a autorisé quoi. <3>La caisse ne sait pas encore le demander partout</3> : sur un droit qu’elle applique sans savoir le demander, ce réglage refuse, et la ligne le dit. Les plafonds de remise en % se règlent dans <4>Référentiels → Remises</4>.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
    </div>
  );
}

// ── LA FICHE D'UN OPÉRATEUR ─────────────────────────────────────────────────────────────────────

function Modal({ width, height, label, children, onClose }: {
  width: number;
  /** Une hauteur FIXE (plafonnée à 92 % de l'écran) — sans elle, la fenêtre prend la hauteur de son contenu. */
  height?: number;
  /** Le nom de la fenêtre pour une aide technique — son titre visible. */
  label: string;
  children: ReactNode;
  onClose: () => void;
}) {
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.42)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label} style={{
        position: 'relative', width: `min(96vw,${width}px)`, ...(height ? { height: `min(92vh,${height}px)` } : { maxHeight: '90vh' }), background: 'var(--paper)',
        borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>{children}</div>
    </div>
  );
}

function SheetHeader({ initials, bg, fg, title, badge, subtitle, onClose }: {
  initials: string; bg: string; fg: string; title: string; badge?: ReactNode; subtitle: string; onClose: () => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px',
      borderBottom: '1px solid var(--ink-200)', flexShrink: 0,
    }}>
      <span style={{
        width: 42, height: 42, borderRadius: 'var(--r-pill)', background: bg, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--t-strong)', fontWeight: 700, flexShrink: 0,
      }}>{initials}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
          {badge}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>{subtitle}</span>
      </span>
      <button type="button" onClick={onClose} aria-label={t('Fermer')} style={{
        width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)',
        color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, cursor: 'pointer',
      }}><Trace d="M6 6l12 12M18 6L6 18" size={15} width={2.2} /></button>
    </div>
  );
}

/**
 * TIRER UN CODE LIBRE — le geste, partagé par la création et le remplacement.
 *
 * ⚠️ LE TIRAGE EST AU SERVEUR, contrairement à la maquette. Elle gardait les PIN en clair dans son
 * état et pouvait donc tirer à l'écran ; ici ils sont HACHÉS, et rien hors du serveur ne sait
 * lesquels sont pris. Un tirage local proposerait un code déjà en service une fois sur cent, et la
 * collision n'apparaîtrait qu'au refus de création — après l'avoir annoncé à quelqu'un.
 * ⚠️ UN ÉCHEC NE POSE PAS DE CODE, et surtout pas un code inventé pour meubler : il laisse le champ
 * en l'état et rend son motif, que l'appelant affiche (§23.0).
 */
function useDrawPin(): [boolean, (poser: (pin: string) => void, echec: (motif: string) => void) => void] {
  const [drawing, setDrawing] = useState(false);
  const tirer = useCallback((poser: (pin: string) => void, echec: (motif: string) => void) => {
    setDrawing(true);
    api.suggestOperatorPin()
      .then((r) => poser(r.pin))
      .catch((e) => echec(e instanceof Error ? e.message : String(e)))
      .finally(() => setDrawing(false));
  }, []);
  return [drawing, tirer];
}

/**
 * LE CODE D'ACCÈS — on le REMPLACE, on ne le révèle jamais.
 *
 * ⚠️ IL N'EXISTE AUCUN CHEMIN QUI RENDE UN PIN, et c'est le but : ils sont hachés. Un gérant qui a
 * « perdu » le code d'un vendeur en pose un nouveau et le lui dit — il ne le retrouve pas. Un champ
 * pré-rempli, même masqué, serait un secret rendu au navigateur.
 * ⚠️ LE CODE TAPÉ RESTE VISIBLE tant qu'il n'est pas enregistré, délibérément : il faut le lire pour
 * pouvoir le transmettre à l'opérateur, et il n'a encore aucune valeur — il n'ouvre rien.
 */
function PinBox({ operator, busy, onSet }: {
  operator: RosterOperator; busy: boolean; onSet: (id: string, pin: string) => Promise<boolean>;
}) {
  const [pin, setPin] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * LE CODE QUI VIENT D'ÊTRE ENREGISTRÉ — gardé à l'écran jusqu'à la fermeture de la fiche.
   *
   * ⚠️ IL ÉTAIT EFFACÉ APRÈS L'ENREGISTREMENT, ET C'ÉTAIT UNE PERTE DE DONNÉE. Le code est haché à
   * la seconde où il part : personne, pas même le serveur, ne peut le relire. L'écran qui vient de le
   * poser en détient donc la SEULE copie lisible — l'effacer au bout d'une demi-seconde obligeait à
   * en générer un autre pour espérer le noter à temps.
   */
  const [saved, setSaved] = useState(false);
  const [drawing, tirer] = useDrawPin();

  /**
   * POSER LE CODE DÈS QU'IL EST COMPLET — mais jamais sans le dire.
   *
   * ⚠️ IL N'Y A PAS DE BOUTON « ENREGISTRER » : un code à quatre chiffres n'a pas d'état
   * intermédiaire, le quatrième chiffre EST la décision. Un bouton de plus ferait confirmer deux fois
   * ce que la boîte de dialogue confirme déjà.
   * ⚠️ LA CONFIRMATION DIT LA CONSÉQUENCE QUI COMPTE : l'ancien code cesse de fonctionner
   * IMMÉDIATEMENT. Un opérateur à qui personne n'a transmis le nouveau se retrouve devant une caisse
   * qui le refuse, sans comprendre — c'est ça qu'il faut avoir en tête avant de valider.
   * ⚠️ ANNULER REMET LE CHAMP À VIDE : y laisser quatre chiffres non enregistrés ferait croire à un
   * code posé (§23.0).
   */
  const poser = useCallback(async (code: string) => {
    const ok = await confirm({
      title: t('Remplacer le code de « {name} » ?', { name: operator.name }),
      message: t('Son ancien code cesse de fonctionner immédiatement. Transmettez-lui le nouveau : sans lui, il ne pourra plus ouvrir la caisse.'),
      confirmLabel: t('Remplacer le code'),
      tone: 'danger',
    });
    if (!ok) { setPin(''); return; }
    // ⚠️ « ENREGISTRÉ » SEULEMENT SI LE SERVEUR L'A ACCEPTÉ : un code refusé (déjà pris…) laisse l'ancien en service, et
    //    l'annoncer posé enverrait le gérant dicter à l'opérateur un code que la caisse refusera.
    if (await onSet(operator.id, code)) setSaved(true);
    else setPin('');
  }, [onSet, operator.id, operator.name]);

  /** La saisie : chiffres seulement, et le quatrième déclenche le geste. */
  const saisir = (brut: string) => {
    const code = brut.replace(/[^0-9]/g, '').slice(0, 4);
    setPin(code);
    setFailure(null);
    setSaved(false);
    if (code.length === 4) void poser(code);
  };

  /**
   * LE MARQUE-PLACE DIT S'IL Y A DÉJÀ UN CODE, et c'est toute la différence.
   * ⚠️ `0000` INVITE À TAPER — il n'a de sens que sur un compte SANS code. Sur un compte qui en a un,
   * il ferait croire que le code est « 0000 » ; les étoiles disent « il y en a un, il ne se relit pas ».
   */
  const fantome = operator.hasPin ? '••••' : '0000';

  return (
    // ⚠️ BORDURE COLORÉE PAR L'ÉTAT (relevé du dessin : `opsFPinBc`) — c'est la tuile entière qui
    //    signale un problème, pas seulement sa ligne de texte.
    <div style={{
      padding: '10px 12px', borderRadius: 'var(--r-sm)',
      border: `1px solid ${failure ? 'var(--rose)' : saved ? 'var(--teal-400)' : 'var(--ink-200)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          flex: 1, fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em',
          textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>{t('Code PIN')}</span>
        {!operator.readOnly && (
          <button type="button" title={t('Générer un code')} aria-label={t('Générer un code')}
            disabled={busy || drawing}
            onClick={() => { setFailure(null); setSaved(false); tirer((code) => { setPin(code); void poser(code); }, setFailure); }}
            style={{
              width: 24, height: 24, border: 'none', borderRadius: 'var(--r-xs)', flexShrink: 0,
              padding: 0, background: 'var(--ink-100)', color: 'var(--ink-600)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}><Trace d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" size={12} width={2} /></button>
        )}
      </div>

      {/* ⚠️ LE FANTÔME EST DESSINÉ, PAS UN `placeholder` : le champ est transparent et sans bordure, et
          un marque-place ordinaire ne se colore qu'en CSS — inatteignable depuis un style en ligne. La
          couche du dessous porte EXACTEMENT la même typographie, donc les chiffres tapés se posent au
          pixel sur ce qu'ils remplacent. */}
      <div style={{ position: 'relative', height: 34, marginTop: 4 }}>
        {pin.length === 0 && (
          <span aria-hidden="true" style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-h2)', fontWeight: 600,
            letterSpacing: '.34em', color: 'var(--ink-300)', pointerEvents: 'none',
          }}>{fantome}</span>
        )}
        <input inputMode="numeric" maxLength={4} aria-label={t('Nouveau code PIN')}
          value={pin} onChange={(e) => saisir(e.target.value)} disabled={operator.readOnly || busy}
          style={{
            position: 'relative', width: '100%', height: '100%', padding: 0, border: 'none',
            background: 'transparent', fontFamily: 'var(--font-mono)',
            fontSize: 'var(--t-h2)', fontWeight: 600, letterSpacing: '.34em',
            color: saved ? 'var(--teal-800)' : 'var(--ink-900)',
            boxSizing: 'border-box', outline: 'none',
          }} />
      </div>

      <div style={{
        fontSize: 'var(--t-sub)', lineHeight: 1.4,
        color: failure ? 'var(--rose-fg)' : saved ? 'var(--teal-800)' : 'var(--ink-500)',
      }}>
        {failure ?? (saved
          ? t('enregistré — notez-le, il ne sera plus lisible')
          : operator.readOnly ? t('se change depuis la Console')
            : drawing ? t('génération…')
              : operator.hasPin ? t('qui connaît ce code est cet opérateur')
                : t('⚠️ aucun code — compte non valide'))}
      </div>
    </div>
  );
}

function OperatorSheet({ operator, templates, busy, onClose, onSave, onToggleActive, onSetPin, onDelete }: {
  operator: RosterOperator;
  templates: readonly RightsTemplateView[];
  busy: boolean;
  onClose: () => void;
  onSave: (id: string, template: string, overrides: Record<string, RightValue>) => Promise<void>;
  onToggleActive: (id: string, next: boolean) => Promise<void>;
  onSetPin: (id: string, pin: string) => Promise<boolean>;
  onDelete: (o: RosterOperator) => Promise<void>;
}) {
  // ⚠️ UN COMPTE SANS MODÈLE N'EN REÇOIT PAS UN D'OFFICE : tant que rien n'est choisi, on ne peut pas
  //    enregistrer — poser « Vendeur » par défaut écrirait un choix que personne n'a fait, et le
  //    premier enregistrement le graverait pour de bon.
  const [template, setTemplate] = useState<string | null>(operator.template);
  /** Le modèle choisi, tel que le magasin l'a réglé : ce sont SES valeurs que le compte suit. */
  const followed = template ? templates.find((x) => x.id === template) ?? null : null;
  /** Relie le libellé du rôle à sa liste — une aide technique annonce le champ par ce nom. */
  const roleId = useId();
  const [overrides, setOverrides] = useState<Record<string, RightValue>>(operator.overrides);

  const dirty = template !== operator.template
    || JSON.stringify(overrides) !== JSON.stringify(operator.overrides);
  const overCount = Object.keys(overrides).length;

  const set = (key: string, v: RightValue) => {
    if (!followed) return;
    const next = { ...overrides };
    if (followed.values[key] === v) delete next[key]; else next[key] = v;
    setOverrides(next);
  };
  const revert = (key: string) => {
    const next = { ...overrides };
    delete next[key];
    setOverrides(next);
  };

  return (
    <Modal width={940} label={operator.name} onClose={onClose}>
      <SheetHeader
        initials={operator.initials}
        bg={operator.active ? 'var(--teal-700)' : 'var(--ink-200)'}
        fg={operator.active ? '#ffffff' : 'var(--ink-500)'}
        title={operator.name}
        badge={<Pill bg={operator.active ? 'var(--teal-100)' : 'var(--ink-100)'}
          fg={operator.active ? 'var(--teal-800)' : 'var(--ink-500)'} height={20}>
          {operator.active ? t('Actif') : t('Désactivé')}
        </Pill>}
        subtitle={t('{n} vente signée · identifiant {id}', { n: operator.sales, id: operator.id })}
        onClose={onClose}
      />

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
        {/* Colonne gauche — identité, modèle, désactivation. */}
        <div style={{
          width: 290, minWidth: 250, flex: '1 1 250px', padding: '16px 18px',
          borderRight: '1px solid var(--ink-100)', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          <div>
            <label htmlFor={roleId} style={{ ...LABEL, display: 'block' }}>{t('Rôle — modèle de départ')}</label>
            {/* ⚠️ CHANGER DE RÔLE EFFACE LES EXCEPTIONS — c'est ce que la phrase dessous promet, et ce que fait la maquette :
                garder des exceptions posées contre l'ANCIEN rôle les appliquerait au nouveau sans que personne l'ait voulu. */}
            <select id={roleId} className="field-input" style={{ height: 38, width: '100%' }}
              value={template ?? ''} disabled={operator.readOnly}
              onChange={(e) => { setTemplate(e.target.value); setOverrides({}); }}>
              {!template && <option value="">{t('— aucun rôle —')}</option>}
              {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{templateName(tpl)}</option>)}
            </select>
            {/* ⚠️ LA PHRASE DIT CE QUE LE GESTE FAIT, et rien d'autre : sans rôle, il faut en choisir
                un ; avec un rôle, en changer REMPLACE les droits. C'est la seule chose qu'un gérant a
                besoin de savoir devant cette liste. */}
            <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)', marginTop: 5, lineHeight: 1.5 }}>
              {!template ? t('Choisissez un rôle pour pouvoir régler ses droits.')
                : overCount > 0 ? t('Changer de rôle efface {n} exception de ce compte.', { n: overCount })
                  : t('Changer de rôle remplace les droits de ce compte.')}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <PinBox operator={operator} busy={busy} onSet={onSetPin} />
            <div style={{ padding: '10px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <div style={{
                fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.05em',
                textTransform: 'uppercase', color: 'var(--ink-500)',
              }}>{t('Ventes signées')}</div>
              <div style={{
                fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)', marginTop: 3,
                fontVariantNumeric: 'tabular-nums',
              }}>{operator.sales.toLocaleString('fr-FR')}</div>
              <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-500)', marginTop: 3, lineHeight: 1.4 }}>
                
                {t('son nom est inscrit en dur dans chaque vente')}
              </div>
            </div>
          </div>

          <div style={{ flex: 1 }} />

          {operator.readOnly ? (
            <div style={{
              padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
              background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55,
            }}>
              <Trans k='Ce compte administre <1>toutes les caisses du parc</1> : il se règle depuis la Console Orgazone, jamais ici.' parts={[(x) => <strong>{x}</strong>]} /></div>
          ) : (
            <div style={{
              padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
              background: 'var(--ink-50)',
            }}>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55, marginBottom: 9 }}>
                {operator.active
                  ? t('Un compte désactivé ne peut plus ouvrir de caisse. Ses ventes passées restent lisibles — il n’est jamais supprimé.')
                  : t('Ce compte ne peut plus ouvrir de caisse.')}
              </div>
              <button type="button" className="btn-ghost" style={{ width: '100%', height: 36 }} disabled={busy}
                onClick={() => { void onToggleActive(operator.id, !operator.active); }}>
                {operator.active ? t('Désactiver le compte') : t('Réactiver le compte')}
              </button>

              {/* ⚠️ OFFERT SEULEMENT SANS AUCUNE VENTE. Un compte qui a encaissé ne se supprime pas :
                  son identifiant est scellé dans des pièces signées, et les effacer rendrait un ticket
                  de l'an dernier illisible. Proposer le bouton pour le refuser ensuite serait une
                  option qui n'aboutit pas (§23.0, interdit n° 6).
                  ⚠️ Le bouton n'est qu'un filtre de bon sens : le VERDICT appartient au serveur, qui
                  mesure aussi les onze autres tables portant un opérateur (sessions, JET, comptages…). */}
              {operator.sales === 0 && (
                <button type="button" style={{
                  width: '100%', height: 34, marginTop: 8, cursor: busy ? 'default' : 'pointer',
                  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                  background: 'var(--paper)', color: 'var(--danger-ink)',
                  fontSize: 'var(--t-body)', fontWeight: 600,
                }} disabled={busy} onClick={() => { void onDelete(operator); }}>
                  
                  {t('Supprimer ce compte')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Colonne droite — les droits, par domaine. */}
        <div style={{
          flex: '2 1 420px', minWidth: 340, padding: '16px 18px',
          display: 'flex', flexDirection: 'column', gap: 11,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ ...LABEL, marginBottom: 0, flex: 1 }}>{t('Droits — tablette & Mobile Partner')}</span>
            {overCount > 0 && !operator.readOnly && (
              <button type="button" className="btn-ghost" style={{ height: 30 }} onClick={() => setOverrides({})}>
                {t('{n} exception — tout réaligner', { n: overCount })}
              </button>
            )}
          </div>

          <RightsNotice />

          {RIGHT_DOMAINS.map((d) => (
            <RightDomainCard key={d.key} domain={d}>
              {d.rights.map((r) => (
                <RightRow key={r.key} def={r}
                  value={followed && r.mode !== 'backoffice' ? overrides[r.key] ?? followed.values[r.key] ?? null : null}
                  overridden={overrides[r.key] !== undefined}
                  readOnly={operator.readOnly || !followed}
                  onSet={(v) => set(r.key, v)}
                  onRevert={() => revert(r.key)} />
              ))}
            </RightDomainCard>
          ))}

          <RightsFooter />
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end',
        padding: '12px 20px', borderTop: '1px solid var(--ink-200)', flexShrink: 0,
      }}>
        {/* ⚠️ Le bouton dit ce qu'il fera VRAIMENT : sans modèle, il n'y a rien à enregistrer. */}
        <span style={{ flex: 1, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
          {!template ? t('Choisissez un modèle pour pouvoir enregistrer.') : dirty ? t('Modifications non enregistrées.') : ''}
        </span>
        <button type="button" className="btn-ghost" onClick={onClose}>{t('Fermer')}</button>
        <button type="button" className="btn-cta" disabled={busy || !dirty || !followed || operator.readOnly}
          onClick={() => { if (followed) void onSave(operator.id, followed.id, overrides); }}>
          
          {t('Enregistrer les droits')}
        </button>
      </div>
    </Modal>
  );
}

// ── L'ONGLET MODÈLES ────────────────────────────────────────────────────────────────────────────

/** Ce qu'une carte de modèle dit de lui en une phrase : le résumé de l'éditeur, ou ce que le magasin en a fait. */
function templateSummary(tpl: RightsTemplateView): string {
  if (!tpl.editor) return t('Créé par le magasin, à partir du modèle {base}.', { base: t(RIGHT_TEMPLATES.find((x) => x.key === tpl.base)!.label) });
  if (tpl.changed.length > 0) return t('{n} droit réglé autrement que chez l’éditeur.', { n: tpl.changed.length });
  return t(TEMPLATE_SUMMARY[tpl.base]);
}

function TemplatesTab({ templates, onOpen, onCreate }: {
  templates: readonly RightsTemplateView[]; onOpen: (id: string) => void; onCreate: () => void;
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', marginBottom: 10,
        border: '1px solid var(--teal-400)', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--teal-800)', display: 'flex', flexShrink: 0, marginTop: 1 }}>
          <Trace d="M12 11v5M12 8v.5" size={16} width={2} />
        </span>
        {/* ⚠️ CE QUE FAIT VRAIMENT UNE MODIFICATION : le dessin dit qu'un modèle « ne touche jamais les comptes existants »
            alors que son code les fait suivre — un compte suit son modèle (cadrage des modèles du magasin, D2). */}
        <span style={{ flex: 1, minWidth: 240, fontSize: 'var(--t-dense)', color: 'var(--teal-800)', lineHeight: 1.55 }}>
          <Trans k='Un compte <1>suit son modèle</1> : modifier un modèle change les droits de tous ses comptes, sauf leurs exceptions. <2>Appliquer aux comptes existants</2> efface aussi ces exceptions.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
        <button type="button" className="btn-ghost" style={{ height: 34, flexShrink: 0 }} onClick={onCreate}>{t('Créer un modèle')}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 10 }}>
        {templates.map((tpl) => {
          const tally = rightsTally(tpl.values);
          const tint = TEMPLATE_TINT[tpl.base];
          return (
            <div key={tpl.id} style={{ ...CARD, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 'var(--r-ctrl)', background: tint.bg, color: tint.fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}><Trace d={templateIcon(tpl.base)} size={17} /></span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{templateName(tpl)}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                    {t('{n} opérateur', { n: tpl.accounts })}
                  </span>
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 9, flexWrap: 'wrap' }}>
                <Pill bg="var(--teal-100)" fg="var(--teal-800)" height={20}>{t('{n} libre', { n: tally.yes })}</Pill>
                <Pill bg="var(--amber-bg)" fg="var(--amber-fg)" height={20}>{t('{n} sous code responsable', { n: tally.ask })}</Pill>
                <Pill bg="var(--ink-100)" fg="var(--ink-500)" height={20}>
                  {t('{n} interdit', { n: tally.no })}
                </Pill>
              </div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55, marginBottom: 10 }}>
                {templateSummary(tpl)}
              </div>
              <button type="button" className="btn-ghost" style={{ width: '100%', height: 36 }}
                onClick={() => onOpen(tpl.id)}>{t('Modifier le modèle')}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * LA FENÊTRE D'UN MODÈLE — ses droits, son nom, et ce qu'on peut en faire.
 *
 * ⚠️ CHAQUE CHOIX S'ENREGISTRE AU CLIC, sans bouton « Enregistrer » : c'est le dessin (« Terminé » ferme), et un
 *    modèle change d'un coup les comptes qui le suivent — l'écran relit ce que le serveur a écrit à chaque geste.
 * ⚠️ LE SERVEUR TRANCHE : un réglage qui retirerait au dernier compte le droit de régler la caisse est refusé, avec son
 *    motif (cadrage des modèles du magasin, D6).
 */
function TemplateSheet({ template, onChanged, onClose }: {
  template: RightsTemplateView;
  onChanged: (v: RosterView) => void;
  onClose: () => void;
}) {
  const tint = TEMPLATE_TINT[template.base];
  const name = templateName(template);
  const nameId = useId();
  const [draftName, setDraftName] = useState(template.label ?? '');
  const [busy, setBusy] = useState(false);
  const paneRef = useRef<HTMLDivElement>(null);
  /** Le domaine qu'on lit : celui du sommaire choisi, puis celui qui arrive en tête du volet en défilant. */
  const [reading, setReading] = useState(RIGHT_DOMAINS[0]!.key);
  /** L'instant du dernier clic au sommaire : le défilement qu'il lance ne doit pas rallumer un autre domaine en route. */
  const jumpedAt = useRef(0);

  useEffect(() => { setDraftName(template.label ?? ''); }, [template.label]);

  /** Un geste sur le modèle : le serveur écrit, l'écran relit, et le toast dit combien de comptes ont suivi. */
  const run = async (title: string, write: () => Promise<{ accounts?: number; roster: RosterView }>, done: (n: number) => string) => {
    setBusy(true);
    try {
      const r = await write();
      onChanged(r.roster);
      showToast(done(r.accounts ?? 0));
      return true;
    } catch (e) {
      await alert({ title, message: e instanceof Error ? e.message : String(e) });
      return false;
    } finally { setBusy(false); }
  };
  const followed = (n: number) => (n > 0
    ? t('Modèle enregistré — {n} compte reçoit ses nouveaux droits.', { n })
    : t('Modèle enregistré.'));

  const setRight = (key: string, v: RightValue) => {
    void run(t('Réglage refusé'), () => api.saveRightsTemplate(template.id, { rights: { [key]: v } }), followed);
  };

  /** Le nom part quand on quitte le champ ; vide, un modèle de l'éditeur reprend le sien. */
  const saveName = () => {
    const v = draftName.trim();
    if (v === (template.label ?? '')) return;
    void run(t('Nom refusé'), () => api.saveRightsTemplate(template.id, { label: v || null }), () => t('Nom du modèle enregistré.'))
      .then((ok) => { if (!ok) setDraftName(template.label ?? ''); });
  };

  const reset = async () => {
    const ok = await confirm({
      title: t('Rétablir le modèle « {name} » ?', { name }),
      message: t('Les droits et les plafonds que le magasin y a changés reviennent aux valeurs de l’éditeur, pour tous ses comptes. Leurs exceptions restent.'),
      confirmLabel: t('Rétablir'),
      tone: 'danger',
    });
    if (ok) await run(t('Rétablissement refusé'), () => api.resetRightsTemplate(template.id), followed);
  };

  const apply = async () => {
    const ok = await confirm({
      title: t('Appliquer aux comptes de « {name} » ?', { name }),
      message: t('{n} compte actif perd ses exceptions : il repart exactement du modèle. Les comptes des autres modèles ne bougent pas.', { n: template.accounts }),
      confirmLabel: t('Appliquer'),
      tone: 'danger',
    });
    if (ok) {
      await run(t('Application refusée'), () => api.applyRightsTemplate(template.id),
        (n) => (n > 0 ? t('{n} compte réaligné sur son modèle.', { n }) : t('Tous ses comptes suivaient déjà le modèle.')));
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: t('Supprimer le modèle « {name} » ?', { name }),
      message: t('Aucun compte ne le suit. Il disparaît de la liste des modèles du magasin.'),
      confirmLabel: t('Supprimer'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      onChanged((await api.deleteRightsTemplate(template.id)).roster);
      showToast(t('Modèle supprimé.'));
      onClose();
    } catch (e) {
      await alert({ title: t('Suppression refusée'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  /** Va au domaine : le VOLET défile, jamais la page (`scrollIntoView` ferait bouger la fenêtre elle-même). */
  const goTo = (domainKey: string) => {
    const pane = paneRef.current;
    const section = pane?.querySelector<HTMLElement>(`[data-domaine="${domainKey}"]`);
    if (pane && section) {
      jumpedAt.current = Date.now();
      pane.scrollTo({ top: Math.max(0, section.offsetTop - pane.offsetTop - 14), behavior: 'smooth' });
    }
    setReading(domainKey);
  };

  /**
   * En lisant, le sommaire suit le domaine arrivé en tête du volet ; tout en bas, c'est le dernier — les derniers
   * domaines, trop courts, ne peuvent plus monter en tête.
   */
  const follow = () => {
    const pane = paneRef.current;
    if (!pane || Date.now() - jumpedAt.current < 1200) return;
    const sections = [...pane.querySelectorAll<HTMLElement>('[data-domaine]')];
    let current = '';
    for (const s of sections) if (s.offsetTop - pane.offsetTop - pane.scrollTop <= 40) current = s.dataset.domaine ?? current;
    if (pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 2 && sections.length) current = sections[sections.length - 1]!.dataset.domaine ?? current;
    if (current) setReading(current);
  };

  return (
    <Modal width={1040} height={860} label={t('Modèle {label}', { label: name })} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--ink-200)', flexShrink: 0 }}>
        <span style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: tint.bg, color: tint.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Trace d={templateIcon(template.base)} size={19} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '16.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Modèle {label}', { label: name })}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2 }}>
            {t('Droits que suivent ses comptes · {n} opérateur aujourd’hui', { n: template.accounts })}
          </span>
        </span>
        <button type="button" onClick={onClose} aria-label={t('Fermer')} style={{
          width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)',
          color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, cursor: 'pointer',
        }}><Trace d="M6 6l12 12M18 6L6 18" size={15} width={2.2} /></button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* ⚠️ Le sommaire disparaît sur un écran étroit (`styles.css`) : la liste prend alors toute la largeur. */}
        <nav aria-label={t('Domaines de droits')} className="oz-rights-nav" style={{
          width: 236, flexShrink: 0, overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', padding: '12px 10px',
          borderRight: '1px solid var(--ink-200)', background: 'var(--ink-50)', display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {RIGHT_DOMAINS.map((d) => {
            const on = d.key === reading;
            return (
              <button key={d.key} type="button" className="oz-rights-nav-item" aria-current={on ? 'location' : undefined} onClick={() => goTo(d.key)}
                style={{
                  boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 10px',
                  border: 'none', borderRadius: 'var(--r-ctrl)', boxShadow: on ? '0 1px 3px rgba(12,14,16,.10)' : 'none',
                  color: on ? 'var(--ink-900)' : 'var(--ink-600)', textAlign: 'left', cursor: 'pointer',
                }}>
                <span style={{ display: 'flex', flexShrink: 0 }}><Trace d={d.icon} size={15} /></span>
                <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: on ? 700 : 500, lineHeight: 1.3 }}>{t(d.label)}</span>
                <span style={{ flexShrink: 0, fontSize: 'var(--t-micro)', fontWeight: 600, color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{d.rights.length}</span>
              </button>
            );
          })}
        </nav>
        <div ref={paneRef} onScroll={follow} data-testid="droits-du-modele" style={{
          flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', padding: '16px 20px 20px',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {/* ⚠️ HORS DU DESSIN : il ne nomme aucun modèle. Un modèle créé par le magasin doit pouvoir porter son nom, et un
              modèle de l'éditeur se renomme (cadrage, D5) — vide, il reprend le sien. */}
          <div style={{ flexShrink: 0 }}>
            <label htmlFor={nameId} style={{ ...LABEL, display: 'block' }}>{t('Nom du modèle')}</label>
            <input id={nameId} className="field-input" style={{ height: 38, width: '100%', maxWidth: 360 }} value={draftName}
              maxLength={40} disabled={busy} placeholder={template.editor ? t(RIGHT_TEMPLATES.find((x) => x.key === template.base)!.label) : ''}
              onChange={(e) => setDraftName(e.target.value)} onBlur={saveName}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
          </div>
          <RightsNotice />
          {RIGHT_DOMAINS.map((d) => (
            <RightDomainCard key={d.key} domain={d} tally={rightsTally(template.values, d.rights)}>
              {d.rights.map((r) => (
                <RightRow key={r.key} def={r} value={r.mode === 'backoffice' ? null : template.values[r.key] ?? null}
                  overridden={template.editor && template.changed.includes(r.key)} readOnly={busy}
                  revertTo="editor"
                  onSet={(v) => setRight(r.key, v)}
                  onRevert={() => { const v = templateValue(template.base, r.key); if (v) setRight(r.key, v); }} />
              ))}
            </RightDomainCard>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 18px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-micro)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          {template.accounts > 0
            ? t('{n} compte actif suit ces droits, sauf ses exceptions.', { n: template.accounts })
            : t('Aucun compte actif ne suit ce modèle.')}
        </span>
        {template.editor && template.stored && (
          <button type="button" className="btn-ghost" disabled={busy} onClick={() => { void reset(); }}>{t('Rétablir le modèle de l’éditeur')}</button>
        )}
        {!template.editor && (
          // ⚠️ GRISÉ TANT QU'UN COMPTE LE SUIT, désactivé compris : le serveur refuserait, et le titre dit pourquoi.
          <button type="button" className="btn-ghost" disabled={busy || template.followers > 0}
            title={template.followers > 0 ? t('Changez de modèle les comptes qui le suivent avant de le supprimer.') : undefined}
            onClick={() => { void remove(); }}>{t('Supprimer le modèle')}</button>
        )}
        <button type="button" className="btn-ghost" disabled={busy || template.accounts === 0} onClick={() => { void apply(); }}>
          {t('Appliquer aux comptes existants')}
        </button>
        <button type="button" className="btn-cta" onClick={onClose}>{t('Terminé')}</button>
      </div>
    </Modal>
  );
}

/** CRÉER UN MODÈLE — un nom, et le modèle dont il copie les droits et les plafonds. */
function NewTemplate({ templates, onClose, onCreated }: {
  templates: readonly RightsTemplateView[];
  onClose: () => void;
  onCreated: (v: RosterView, id: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [from, setFrom] = useState('vend');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const fieldId = useId();

  const submit = async () => {
    setBusy(true); setFailure(null);
    try {
      const r = await api.createRightsTemplate(label.trim(), from);
      onCreated(r.roster, r.id);
      showToast(t('Modèle créé — réglez ses droits, puis choisissez-le pour un compte.'));
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal width={470} label={t('Nouveau modèle de droits')} onClose={onClose}>
      <div style={{ padding: '22px 22px 18px' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Nouveau modèle de droits')}</h3>
        <p style={{ margin: '4px 0 14px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          {t('Il part des droits et des plafonds de remise d’un modèle existant ; vous les réglez ensuite.')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label htmlFor={`${fieldId}-label`} style={{ ...LABEL, display: 'block' }}>{t('Nom du modèle')}</label>
            <input id={`${fieldId}-label`} className="field-input" style={{ height: 38, width: '100%' }} value={label} maxLength={40}
              onChange={(e) => setLabel(e.target.value)} placeholder={t('Vendeur saisonnier')} />
          </div>
          <div>
            <label htmlFor={`${fieldId}-from`} style={{ ...LABEL, display: 'block' }}>{t('À partir du modèle')}</label>
            <select id={`${fieldId}-from`} className="field-input" style={{ height: 38, width: '100%' }} value={from}
              onChange={(e) => setFrom(e.target.value)}>
              {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{templateName(tpl)}</option>)}
            </select>
          </div>
          {failure && (
            <div role="alert" style={{ fontSize: 'var(--t-dense)', color: 'var(--danger-ink)', lineHeight: 1.5 }}>{failure}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button type="button" className="btn-cta" disabled={busy || !label.trim()} onClick={() => { void submit(); }}>{t('Créer le modèle')}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── LA CRÉATION ─────────────────────────────────────────────────────────────────────────────────

function NewOperator({ templates, onClose, onCreated }: {
  templates: readonly RightsTemplateView[]; onClose: () => void; onCreated: (v: RosterView) => void;
}) {
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<string>('vend');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [drawing, tirer] = useDrawPin();
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();

  // ⚠️ UN CODE EST TIRÉ À L'OUVERTURE, parce que c'est le cas courant : personne ne CHOISIT un code
  //    à quatre chiffres, on en veut un qui soit libre. Le champ reste éditable — le tirage est une
  //    proposition, pas une contrainte.
  // ⚠️ SI LE TIRAGE ÉCHOUE, LE CHAMP RESTE VIDE et le motif s'affiche : poser un code inventé pour
  //    meubler ferait échouer la création plus tard, après l'avoir annoncé à l'opérateur (§23.0).
  useEffect(() => { tirer(setPin, setFailure); }, [tirer]);

  // ⚠️ CE QUE L'ÉCRAN PEUT DIRE SEUL, il le dit ; l'unicité, elle, appartient au serveur — le
  //    navigateur ne connaît pas les codes des autres comptes, et c'est exactement ce qu'on veut.
  const localWhy = /^[0-9]*$/.test(pin) ? (pin.length && pin.length !== 4 ? t('Un PIN fait 4 chiffres.') : '')
    : t('Chiffres uniquement — le clavier de la caisse n’a que ça.');

  const submit = async () => {
    setBusy(true); setFailure(null);
    try {
      onCreated(await api.createOperator(name.trim(), template, pin));
      showToast(t('Compte créé — il descend aux caisses du magasin.'));
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal width={470} label={t('Nouvel opérateur')} onClose={onClose}>
      <div style={{ padding: '22px 22px 18px' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Nouvel opérateur')}</h3>
        <p style={{ margin: '4px 0 14px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
          
          {t('Le modèle pose les droits de départ. Vous pourrez tout ajuster ensuite, droit par droit.')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label htmlFor={`${fieldId}-name`} style={{ ...LABEL, display: 'block' }}>{t('Nom affiché en caisse')}</label>
            <input id={`${fieldId}-name`} className="field-input" style={{ height: 38, width: '100%' }} value={name}
              onChange={(e) => setName(e.target.value)} placeholder={t('Prénom Nom')} />
          </div>
          <div>
            <label htmlFor={`${fieldId}-template`} style={{ ...LABEL, display: 'block' }}>{t('Modèle de droits')}</label>
            <select id={`${fieldId}-template`} className="field-input" style={{ height: 38, width: '100%' }} value={template}
              onChange={(e) => setTemplate(e.target.value)}>
              {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{templateName(tpl)}</option>)}
            </select>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor={`${fieldId}-pin`} style={{ ...LABEL, marginBottom: 0, flex: 1 }}>{t('Code PIN — 4 chiffres, unique')}</label>
              <button type="button" className="btn-ghost" style={{ height: 26 }} disabled={busy || drawing}
                onClick={() => { setFailure(null); tirer(setPin, setFailure); }}>
                {drawing ? t('Génération…') : t('Générer un code')}
              </button>
            </div>
            <input id={`${fieldId}-pin`} className="field-input" inputMode="numeric" maxLength={4} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder={drawing ? '…' : ''}
              style={{
                height: 44, width: '100%', marginTop: 6, fontFamily: 'var(--font-mono)',
                fontSize: 'var(--t-h2)', fontWeight: 600, letterSpacing: '.34em',
              }} />
            {localWhy && (
              <div style={{ fontSize: 'var(--t-micro)', color: 'var(--amber-fg)', marginTop: 5 }}>{localWhy}</div>
            )}
          </div>
          <div style={{
            display: 'flex', gap: 8, padding: '10px 12px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
            fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            <Trace d="M8 11V7a4 4 0 0 1 8 0v4" size={13} width={2} />
            <span>
              <Trans k='Ce code <1>remplace un identifiant et un mot de passe</1> : en caisse, on ne choisit pas de nom dans une liste — <2>qui tape ce code est cet opérateur</2>, et les ventes sont signées à son nom. Le compte est créé <3>dans ce magasin</3>.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
          </div>
          {failure && (
            <div role="alert" style={{ fontSize: 'var(--t-dense)', color: 'var(--danger-ink)', lineHeight: 1.5 }}>{failure}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button type="button" className="btn-cta" disabled={busy || !name.trim() || pin.length !== 4}
            onClick={() => { void submit(); }}>{t('Créer le compte')}</button>
        </div>
      </div>
    </Modal>
  );
}
